import React, { useState, useRef, useEffect } from 'react';
import '../../assets/styles/timepicker-custom.css';
import { FaClock, FaTimes } from 'react-icons/fa';

const applyTimeMask = (value) => {
    let cleaned = value.replace(/\D/g, '');

    if (cleaned.length > 4) {
        cleaned = cleaned.substring(0, 4);
    }

    let formatted = '';
    if (cleaned.length > 0) {
        formatted = cleaned.substring(0, 2);
        if (cleaned.length > 2) {
            formatted += ':' + cleaned.substring(2, 4);
        }
    }

    return formatted;
};

const parseInputToTime = (inputValue) => {
    if (!inputValue) return null;

    const parts = inputValue.split(':');
    if (parts.length !== 2) return null;

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    if (isNaN(hours) || isNaN(minutes)) return null;
    if (hours < 0 || hours > 23) return null;
    if (minutes < 0 || minutes > 59) return null;

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
};

const TimePicker = ({
    value,
    onChange,
    placeholder = "hh:mm",
    className = "form-control",
    timeIntervals = 15,
    disabled = false,
    strategy = "auto",
    isClearable = true
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [displayValue, setDisplayValue] = useState('');
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        if (value) {
            const hours = value.getHours().toString().padStart(2, '0');
            const minutes = value.getMinutes().toString().padStart(2, '0');
            setDisplayValue(`${hours}:${minutes}`);
        } else {
            setDisplayValue('');
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        const handleScroll = (event) => {
            if (isOpen && listRef.current && !listRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        const handleResize = () => {
            if (isOpen) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [isOpen]);

    const generateTimeOptions = () => {
        const times = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += timeIntervals) {
                const time = new Date();
                time.setHours(hour, minute, 0, 0);
                times.push(time);
            }
        }
        return times;
    };

    const handleTimeSelect = (time) => {
        onChange(time);
        setIsOpen(false);
    };

    const handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        onChange(null);
        setDisplayValue('');
        setIsOpen(false);
    };

    const handleInputChange = (e) => {
        if (disabled) return;
        const rawValue = e.target.value;
        const masked = applyTimeMask(rawValue);
        setDisplayValue(masked);

        const parsedTime = parseInputToTime(masked);
        if (parsedTime) {
            onChange(parsedTime);
        } else if (masked === '') {
            onChange(null);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handleClockClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;

        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const isMobile = window.innerWidth <= 768;
            const dropdownHeight = isMobile ? 280 : 300;
            const dropdownWidth = isMobile ? 200 : 150;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            let top;
            let left;

            if (isMobile) {
                left = (viewportWidth - dropdownWidth) / 2;

                if (spaceBelow >= dropdownHeight) {
                    top = rect.bottom + 5;
                } else if (spaceAbove >= dropdownHeight) {
                    top = rect.top - dropdownHeight - 5;
                } else {
                    top = (viewportHeight - dropdownHeight) / 2;
                }
            } else {
                if (strategy === "fixed") {
                    left = rect.left;

                    if (spaceAbove >= dropdownHeight && spaceAbove > spaceBelow) {
                        top = rect.top - dropdownHeight - 2;
                    } else {
                        top = rect.bottom + 2;
                    }
                } else {
                    left = rect.left + window.scrollX;

                    if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
                        top = rect.bottom + window.scrollY + 2;
                    } else {
                        top = rect.top + window.scrollY - dropdownHeight - 2;
                    }
                }

                if (left + dropdownWidth > viewportWidth) {
                    left = viewportWidth - dropdownWidth - 10;
                }
                if (left < 0) {
                    left = 10;
                }
            }

            if (strategy === "fixed" || isMobile) {
                if (top < 0) {
                    top = 10;
                }
                if (top + dropdownHeight > viewportHeight) {
                    top = viewportHeight - dropdownHeight - 10;
                }
            } else {
                if (top < window.scrollY) {
                    top = window.scrollY + 10;
                }
                if (top + dropdownHeight > window.scrollY + viewportHeight) {
                    top = window.scrollY + viewportHeight - dropdownHeight - 10;
                }
            }

            setDropdownPosition({
                top: Math.max(0, top),
                left: Math.max(0, left)
            });
        }
        setIsOpen(!isOpen);
    };

    const scrollToSelectedTime = () => {
        if (value && listRef.current) {
            const selectedTime = formatTime(value);
            const timeOptions = generateTimeOptions();
            const selectedIndex = timeOptions.findIndex(time => formatTime(time) === selectedTime);

            if (selectedIndex !== -1) {
                const itemHeight = listRef.current.scrollHeight / timeOptions.length;
                const listHeight = listRef.current.clientHeight;
                const scrollTop = (selectedIndex * itemHeight) - (listHeight / 2) + (itemHeight / 2);
                listRef.current.scrollTop = Math.max(0, scrollTop);
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            setTimeout(scrollToSelectedTime, 50);
        }
    }, [isOpen, value]);

    const formatTime = (time) => {
        const hours = time.getHours().toString().padStart(2, '0');
        const minutes = time.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const timeOptions = generateTimeOptions();

    return (
        <div className="timepicker-container" ref={dropdownRef}>
            <div className="input-group">
                <input
                    ref={inputRef}
                    type="text"
                    className={`${className} ${disabled ? 'disabled' : ''}`}
                    value={displayValue}
                    placeholder={placeholder}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    maxLength={5}
                    autoComplete="off"
                />
                {isClearable && displayValue && !disabled && (
                    <button
                        type="button"
                        className="btn btn-clear"
                        onClick={handleClear}
                        title="Limpar"
                    >
                        <FaTimes />
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-calendar"
                    onClick={handleClockClick}
                    title="Selecionar horário"
                    disabled={disabled}
                >
                    <FaClock />
                </button>
            </div>

            {isOpen && (
                <div
                    className="timepicker-dropdown"
                    style={{
                        top: dropdownPosition.top,
                        left: dropdownPosition.left
                    }}
                >
                    <div className="timepicker-header">
                        Selecione um horário
                    </div>
                    <div className="timepicker-list" ref={listRef}>
                        {timeOptions.map((time, index) => (
                            <div
                                key={index}
                                className={`timepicker-item ${
                                    value && formatTime(time) === formatTime(value) ? 'selected' : ''
                                }`}
                                onClick={() => handleTimeSelect(time)}
                            >
                                {formatTime(time)}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimePicker;
